import { useMemo, type ReactNode } from "react";
import { useAnnouncer } from "@design-system/LiveRegion/LiveRegion";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import type { BoardCardShortcut, BoardProps } from "./Board.types";
import {
    createColumnAdding,
    hasAnyGapAboveFirst,
    type BoardColumnAddingResolver,
} from "./boardAdd";
import { getBoardKeyboardCopy } from "./boardAnnouncements";
import { placementKeyProps } from "./boardAttributes";
import {
    BoardCardExpansionContext,
    BoardCardItemIdContext,
    createBoardCardExpansionStore,
} from "./boardCardExpansion";
import { buildBoardCells, getCellEntries } from "./boardCells";
import { BoardCellList, type BoardEntrySlot } from "./BoardCellList";
import { createBoardCollapse } from "./boardCollapse";
import { BoardCollapsedColumn } from "./BoardCollapsedColumn";
import {
    BoardEnteringContext,
    useBoardCardEntrance,
    useBoardEntering,
} from "./boardEntrance";
import { BoardLayout } from "./BoardLayout";
import { BoardCardKeyContext } from "./boardRoving";
import { DraggableBoard } from "./DraggableBoard";
import {
    BoardSelectionContext,
    useBoardSelection,
    useIsBoardItemSelected,
} from "./useBoardSelection";
import styles from "./Board.module.scss";

const NO_PINS = { pinnedKeys: [] };
const NO_SHORTCUTS: readonly BoardCardShortcut[] = [];

interface BoardStaticSlotProps {
    itemId: string;
    placementKey: string;
    slot: BoardEntrySlot;
    renderCard: (isSelected: boolean) => ReactNode;
}

/** A card's `li` on a board without drag and drop. */
const BoardStaticSlot = ({
    itemId,
    placementKey,
    slot,
    renderCard,
}: Readonly<BoardStaticSlotProps>) => {
    const isSelected = useIsBoardItemSelected(itemId);
    const entranceRef = useBoardCardEntrance(itemId, slot);
    const { measureRef } = slot;
    const ref = useMemo(
        () => mergeRefs<HTMLElement>([measureRef, entranceRef]),
        [measureRef, entranceRef],
    );
    return (
        <li
            ref={ref}
            data-index={slot.index}
            {...placementKeyProps(placementKey)}
            className={styles.cardSlot}
        >
            {slot.gapAdds}
            <BoardCardItemIdContext.Provider value={itemId}>
                <BoardCardKeyContext.Provider value={placementKey}>
                    {renderCard(isSelected)}
                </BoardCardKeyContext.Provider>
            </BoardCardItemIdContext.Provider>
        </li>
    );
};

/**
 * Kanban layout engine: fixed-width columns (one 340px card) that scroll on their
 * own under sticky headers, inside a horizontally scrolling board; with
 * `lanes`, rows of cells across the columns and one scroll for the board. Data in,
 * intent out: the surface filters `items`, renders each card and, with
 * `onMove`, applies the one move each drag reports on drop.
 *
 * Callbacks must be stable (module scope or `useCallback`); they key the cell memo.
 *
 * @example
 * const getTaskId = (task: Task) => task.id;
 * const getTaskPlacements = (task: Task) => [
 *     { columnId: task.stageId, laneId: null },
 * ];
 *
 * <Board
 *     aria-label="Close tasks"
 *     items={visibleTasks}
 *     columns={stageColumns}
 *     getItemId={getTaskId}
 *     getItemLabel={(task) => task.title}
 *     getPlacements={getTaskPlacements}
 *     renderCard={(task) => <TaskBoardCard task={task} />}
 *     onMove={({ item, to }) => updateStage(item, to.columnId)}
 * />
 */
export const Board = <TItem,>({
    "aria-label": ariaLabel,
    items,
    columns,
    lanes,
    hasCommandIsland = false,
    getItemId,
    getItemLabel,
    getPlacements,
    renderCard,
    compareItems,
    sortHint,
    getMovedItem,
    emptyColumnLabel = "No items",
    onMove,
    onMoveMany,
    hasMultiValueLanes = false,
    getLock,
    canMove,
    onAddItem,
    getAddLabel,
    canPlaceBetween = compareItems === undefined,
    canPlaceAt,
    canAddBetween = canPlaceBetween &&
        (compareItems === undefined || canPlaceAt === undefined),
    enteringItemIds,
    enteringReveal = "scroll",
    collapsedColumnIds,
    collapsedLaneIds,
    onCollapsedChange,
    selectedItemIds,
    onSelectionChange,
    cardShortcuts = NO_SHORTCUTS,
    initialFocusedItemId,
}: Readonly<BoardProps<TItem>>) => {
    const cells = useMemo(
        () =>
            buildBoardCells({ items, getItemId, getPlacements, compareItems }),
        [items, getItemId, getPlacements, compareItems],
    );
    const collapse = useMemo(
        () =>
            createBoardCollapse({
                collapsedColumnIds,
                collapsedLaneIds,
                onCollapsedChange,
            }),
        [collapsedColumnIds, collapsedLaneIds, onCollapsedChange],
    );
    const entering = useBoardEntering(enteringItemIds, enteringReveal);
    const expansion = useMemo(() => createBoardCardExpansionStore(), []);
    const announcer = useAnnouncer();
    const selection = useBoardSelection({
        cells,
        selectedItemIds,
        onSelectionChange,
        announce: announcer.announce,
    });
    const keyboardCopy = useMemo(
        () =>
            getBoardKeyboardCopy({
                isDraggable: onMove !== undefined,
                canAdd: onAddItem !== undefined,
                hasLanes: lanes !== undefined,
                canSelect: onSelectionChange !== undefined,
                shortcuts: cardShortcuts,
            }),
        [onMove, onAddItem, lanes, onSelectionChange, cardShortcuts],
    );
    const hasGapAboveFirst = hasAnyGapAboveFirst({
        columns,
        hasLanes: lanes !== undefined,
        onAddItem,
        canPlaceBetween: canAddBetween,
    });
    const getColumnAdding: BoardColumnAddingResolver<TItem> = (column, lane) =>
        createColumnAdding({
            column,
            lane,
            getItemLabel,
            onAddItem,
            getAddLabel,
            canPlaceBetween: canAddBetween,
        });

    return (
        <BoardEnteringContext.Provider value={entering}>
            <BoardCardExpansionContext.Provider value={expansion}>
                <BoardSelectionContext.Provider value={selection}>
                    {onMove === undefined ? (
                        <BoardLayout
                            aria-label={ariaLabel}
                            columns={columns}
                            lanes={lanes}
                            hasCommandIsland={hasCommandIsland}
                            cells={cells}
                            collapse={collapse}
                            hasGapAboveFirst={hasGapAboveFirst}
                            getColumnAdding={getColumnAdding}
                            announcer={announcer}
                            keyboardCopy={keyboardCopy}
                            cardShortcuts={cardShortcuts}
                            initialFocusedItemId={initialFocusedItemId}
                            renderCell={({
                                column,
                                lane,
                                placement,
                                labelledBy,
                                isWindowed,
                            }) => (
                                <BoardCellList
                                    placement={placement}
                                    labelledBy={labelledBy}
                                    entries={getCellEntries(cells, placement)}
                                    windowing={isWindowed ? NO_PINS : undefined}
                                    renderEntry={(entry, slot) => (
                                        <BoardStaticSlot
                                            key={entry.placementKey}
                                            itemId={entry.itemId}
                                            placementKey={entry.placementKey}
                                            slot={slot}
                                            renderCard={(isSelected) =>
                                                renderCard(entry.item, {
                                                    placement: entry.placement,
                                                    placementKey:
                                                        entry.placementKey,
                                                    isSelected,
                                                })
                                            }
                                        />
                                    )}
                                    adding={getColumnAdding(column, lane)}
                                    emptyLabel={
                                        lane === null ? emptyColumnLabel : null
                                    }
                                />
                            )}
                            renderCollapsedColumn={({
                                column,
                                ...stripProps
                            }) => (
                                <BoardCollapsedColumn
                                    icon={column.icon}
                                    {...stripProps}
                                />
                            )}
                        />
                    ) : (
                        <DraggableBoard
                            aria-label={ariaLabel}
                            cells={cells}
                            columns={columns}
                            lanes={lanes}
                            hasCommandIsland={hasCommandIsland}
                            collapse={collapse}
                            hasGapAboveFirst={hasGapAboveFirst}
                            renderCard={renderCard}
                            getItemLabel={getItemLabel}
                            compareItems={compareItems}
                            sortHint={sortHint}
                            getMovedItem={getMovedItem}
                            canPlaceBetween={canPlaceBetween}
                            canPlaceAt={canPlaceAt}
                            emptyColumnLabel={emptyColumnLabel}
                            onMove={onMove}
                            onMoveMany={onMoveMany}
                            hasMultiValueLanes={hasMultiValueLanes}
                            getLock={getLock}
                            canMove={canMove}
                            getColumnAdding={getColumnAdding}
                            announcer={announcer}
                            keyboardCopy={keyboardCopy}
                            cardShortcuts={cardShortcuts}
                            initialFocusedItemId={initialFocusedItemId}
                        />
                    )}
                </BoardSelectionContext.Provider>
            </BoardCardExpansionContext.Provider>
        </BoardEnteringContext.Provider>
    );
};
